import { apiClient } from './api-client';

/**
 * A therapy event returned by the backend: /events/custom for loose uploads, the
 * combined /therapy-archive endpoints for container archives. Times are offset-less
 * wall-clock ISO strings (yyyy-MM-ddTHH:mm:ss.FFF — the fraction appears only when
 * non-zero), parsed with the same local Date construction as EDF start times so
 * events line up with the signals. `code` is only meaningful together with
 * `generation` (P5 plugin codes differ from the RespEventID space shared by
 * P2.8/P3/P3.2).
 */
export type TherapyEventDto = {
  generation: 'p28' | 'p3' | 'p32' | 'p5' | string;
  code: number | null;
  name: string;
  kind: 'respiratory' | 'alarm' | 'device' | string;
  startLocal: string;
  endLocal: string;
  isAlarm: boolean;
  limit: number | null;
  value: number | null;
};

/**
 * A "custom" upload (containerType 'custom' in file-upload) is loose
 * .edf/.wmedf files dropped together with their event XMLs, as opposed to a
 * device archive container whose events the backend extracts itself. The
 * backend never sees the loose signal files, so each XML travels with the
 * header start/duration of its paired EDF record.
 */
export type CustomEventsRecord = {
  fileName: string;
  /** The loose event XML, uploaded as-is. */
  file: File;
  /** Offset-less local ISO (yyyy-MM-ddTHH:mm:ss.FFF), same convention as TherapyEventDto times. */
  startLocal: string;
  durationMs: number;
};

// Unlike the archive endpoints, /events/custom is awaited inside the upload
// mutation, so a hung backend must not stall the upload UI forever.
const CUSTOM_EVENTS_TIMEOUT_MS = 60_000;

export async function getCustomEvents(
  records: CustomEventsRecord[],
): Promise<TherapyEventDto[]> {
  const formData = new FormData();
  records.forEach((record, idx) => {
    formData.append(`Records[${idx}].File`, record.file, record.fileName);
    formData.append(`Records[${idx}].StartLocal`, record.startLocal);
    formData.append(
      `Records[${idx}].DurationMs`,
      String(Math.round(record.durationMs)),
    );
  });

  const response = await apiClient.post<TherapyEventDto[]>(
    '/events/custom',
    formData,
    { timeout: CUSTOM_EVENTS_TIMEOUT_MS },
  );
  return response.data;
}
